import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { StyleSheet, View, Pressable, Platform, Alert } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme, PLUS_COLORS } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { useAuthStore, useCreditPacksStore, useModalStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { getCreditPackIapProductId } from '@/hooks/useIosIap';
import useTranslation from '@/hooks/useTranslation';
import { trackEvent } from '@/constants/utils/analytics';
import { PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY, PRICING_PLANS } from '@/constants/config';
import {
  getPlusCreditOptions,
  formatCreditResetDate,
  daysUntilCreditReset,
  startAryaChat,
} from '@/lib/plusCreditOptions';

const PREMIUM_PURPLE = '#7C3AED';

const createStyles = (colors, isDark, compact) => StyleSheet.create({
  container: {
    gap: compact ? 8 : 10,
  },
  refreshRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: compact ? 6 : 8,
    paddingHorizontal: compact ? 10 : 12,
    borderRadius: 10,
    backgroundColor: colors.solid_color_tertiary,
  },
  refreshText: {
    flex: 1,
    lineHeight: 18,
  },
  optionCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    padding: compact ? 10 : 12,
    gap: compact ? 8 : 10,
  },
  optionCardHighlight: {
    borderColor: isDark ? 'rgba(34, 197, 94, 0.45)' : 'rgba(34, 197, 94, 0.55)',
    backgroundColor: isDark ? 'rgba(34, 197, 94, 0.07)' : 'rgba(34, 197, 94, 0.05)',
  },
  optionCardPremium: {
    borderColor: isDark ? 'rgba(124, 58, 237, 0.5)' : 'rgba(124, 58, 237, 0.4)',
    backgroundColor: PLUS_COLORS.backgroundColorPurple,
  },
  optionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  optionIcon: {
    width: compact ? 28 : 32,
    height: compact ? 28 : 32,
    borderRadius: compact ? 8 : 9,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  optionText: {
    flex: 1,
    gap: 1,
  },
  freeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(34, 197, 94, 0.4)',
  },
  packRow: {
    flexDirection: 'row',
    gap: 6,
    flexWrap: 'wrap',
  },
  pack: {
    flex: 1,
    minWidth: 78,
    alignItems: 'center',
    paddingVertical: compact ? 7 : 9,
    paddingHorizontal: 6,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_tertiary,
  },
  packHovered: {
    borderColor: colors.accent_color_primary,
    backgroundColor: colors.accent_color_primary + '10',
  },
  packPopular: {
    borderColor: colors.accent_color_primary + '60',
  },
  packBadge: {
    position: 'absolute',
    top: -7,
    right: -3,
    backgroundColor: colors.accent_color_primary,
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 4,
  },
  inlineActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  linkText: {
    textDecorationLine: 'underline',
  },
});

/**
 * Every path forward for a Plus / Plus Premium subscriber whose credits are
 * spent, laid out as explicit choices:
 *
 *   1. Buy more credits (quick packs + full store)
 *   2. Upgrade to Plus Premium (regular Plus on web only)
 *   3. Keep chatting with Arya for free
 *
 * plus the date the monthly allotment refreshes. Reused by the out-of-credits
 * modal, the inline chat error card, Work's credit gate, tool footers and the
 * voice modal so the story is identical on every surface.
 *
 * `onBeforeAction` lets a host modal close itself before another modal opens.
 */
function PlusOutOfCreditsOptions({
  compact = false,
  source = 'out_of_credits',
  onBeforeAction,
  showPacks = true,
  showArya = true,
  aryaLabel,
  packLimit = 3,
  dialog = false,
}) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const styles = useMemo(() => createStyles(colors, isDark, compact), [colors, isDark, compact]);
  const { t } = useTranslation();
  const { openModal } = useModalStore(state => state.actions);
  const { openCreditPacksCheckout, iap } = useGabPurchase();
  const user = useAuthStore(state => state.user);
  const [loadingPackId, setLoadingPackId] = useState(null);
  const [purchaseError, setPurchaseError] = useState('');
  const isWork = source.includes('work');

  const options = useMemo(() => getPlusCreditOptions(user), [user]);
  const { isPlusPremium, canUpgradeToPremium, monthlyTotal, resetDate } = options;
  const resetLabel = formatCreditResetDate(resetDate);
  const daysLeft = daysUntilCreditReset(resetDate);

  const creditPacks = useCreditPacksStore(state => state.creditPacks);
  const fetchCreditPacks = useCreditPacksStore(state => state.fetchCreditPacks);
  useEffect(() => {
    if (showPacks && !creditPacks?.length) fetchCreditPacks();
  }, [showPacks, creditPacks, fetchCreditPacks]);

  const displayPacks = useMemo(
    () => (showPacks && creditPacks?.length ? creditPacks.slice(0, packLimit) : []),
    [showPacks, creditPacks, packLimit],
  );

  useEffect(() => {
    trackEvent('conversion', 'plus_out_of_credits_options_shown', source, isPlusPremium ? 1 : 0);
  }, []);

  const getPackPrice = useCallback((pack) => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      const productId = getCreditPackIapProductId(pack.credits);
      const product = productId ? iap?.getProduct?.(productId) : null;
      if (product?.localizedPrice) return product.localizedPrice;
      if (product?.price != null) return `$${parseFloat(product.price).toFixed(2)}`;
    }
    return Number(pack.price).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: Number.isInteger(Number(pack.price)) ? 0 : 2 });
  }, [iap]);

  const handleChatWithArya = useCallback(() => {
    onBeforeAction?.();
    startAryaChat({ source });
  }, [onBeforeAction, source]);

  const handleBuyCredits = useCallback(() => {
    trackEvent('conversion', 'out_of_credits_buy_credits', source);
    onBeforeAction?.();
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  }, [onBeforeAction, openModal, source]);

  const handleUpgradePremium = useCallback(() => {
    trackEvent('conversion', 'out_of_credits_upgrade_premium', source);
    onBeforeAction?.();
    openModal('PricingModal', { initialCadence: 'monthly' });
  }, [onBeforeAction, openModal, source]);

  const handleSelectPack = useCallback(async (pack) => {
    const packId = pack?.id ?? pack;
    setLoadingPackId(packId);
    setPurchaseError('');
    trackEvent('conversion', 'out_of_credits_quick_pack', source, pack?.credits);
    try {
      const result = await openCreditPacksCheckout({ creditPackId: packId, credits: pack?.credits });
      if (result?.success) onBeforeAction?.();
    } catch (error) {
      if (/checkout (?:closed|cancelled|canceled)|user closed|backdrop close/i.test(error?.message || '')) return;
      console.error('[PlusOutOfCreditsOptions] Purchase error:', error);
      setPurchaseError(t('plusOutOfCredits.checkoutError'));
      if (Platform.OS !== 'web') {
        Alert.alert(t('messages.creditsDepleted.purchaseError'), error?.message || t('messages.creditsDepleted.purchaseErrorMsg'));
      }
    } finally {
      setLoadingPackId(null);
    }
  }, [openCreditPacksCheckout, onBeforeAction, source, t]);

  const refreshCopy = useMemo(() => {
    const total = monthlyTotal.toLocaleString();
    if (!resetLabel) {
      return t('plusOutOfCredits.refreshOnRenewal', { total });
    }
    if (daysLeft === 0) {
      return t('plusOutOfCredits.refreshToday', { total });
    }
    if (daysLeft === 1) {
      return t('plusOutOfCredits.refreshTomorrow', { total, date: resetLabel });
    }
    return t('plusOutOfCredits.refreshOn', { total, date: resetLabel, days: daysLeft });
  }, [monthlyTotal, resetLabel, daysLeft, t]);

  if (dialog) {
    return (
      <View style={{ gap: 12 }}>
        <View style={[styles.optionCard, { padding: 16, gap: 12 }]}>
          <View style={styles.optionText}>
            <Text size="n" weight="semibold">{t('plusOutOfCredits.topUpOnce')}</Text>
            <Text size="xs" color="secondary">{t('plusOutOfCredits.topUpOnceSubtitle')}</Text>
          </View>
          <View style={styles.packRow}>
            {displayPacks.map((pack) => (
              <Pressable key={pack.id} onPress={() => handleSelectPack(pack)} disabled={loadingPackId !== null}
                accessibilityRole="button" accessibilityLabel={`${pack.credits?.toLocaleString()} credits for ${getPackPrice(pack)}`}
                style={({ hovered }) => [styles.pack, { paddingVertical: 10 }, hovered && styles.packHovered]}>
                <Text size="n" weight="semibold">{pack.credits?.toLocaleString()} <Text size="xs" color="secondary">{t('messages.creditsDepleted.credits')}</Text></Text>
                <Text size="s" weight="semibold" style={{ marginTop: 4 }}>{loadingPackId === pack.id ? '…' : getPackPrice(pack)}</Text>
              </Pressable>
            ))}
          </View>
          <Pressable onPress={handleBuyCredits} accessibilityRole="button" style={{ alignSelf: 'flex-start' }}>
            <Text size="xs" color="secondary" style={styles.linkText}>{t('plusOutOfCredits.seeAllPacks')}</Text>
          </Pressable>
        </View>
        {canUpgradeToPremium && (
          <View style={[styles.optionCard, styles.optionCardPremium, { padding: 16, gap: 10 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <View style={{ flex: 1, gap: 3 }}>
                <Text size="n" weight="semibold">{t('plusOutOfCredits.premiumTitle')}</Text>
                <Text size="xs" color="secondary">{t('plusOutOfCredits.premiumMonthlySubtitle', { credits: PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY, price: PRICING_PLANS.plusPremium.monthlyPrice })}</Text>
              </View>
              <View style={[styles.optionIcon, { backgroundColor: PREMIUM_PURPLE }]}><Icon name="gem" size={16} color="#FFFFFF" solid /></View>
            </View>
            <Button variant="plus" text={t('plusOutOfCredits.explorePremium')} size="small"
              onPress={handleUpgradePremium} isFullWidth />
          </View>
        )}
        {!!purchaseError && <View accessibilityRole="alert" accessibilityLiveRegion="polite"><Text size="xs" color="secondary">{purchaseError}</Text></View>}
        <View style={{ gap: 7, alignItems: 'center', paddingTop: 2 }}>
          <Text size="xs" color="secondary" style={{ textAlign: 'center' }}>{resetLabel
            ? t('plusOutOfCredits.refreshShort', { total: monthlyTotal.toLocaleString(), date: resetLabel })
            : t('plusOutOfCredits.refreshOnRenewal', { total: monthlyTotal.toLocaleString() })}</Text>
          {showArya && <Pressable onPress={handleChatWithArya} accessibilityRole="button" style={{ paddingVertical: 4 }}>
            <Text size="xs" color="secondary" style={styles.linkText}>{t('plusOutOfCredits.aryaWorkAlternative')}</Text>
          </Pressable>}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.refreshRow}>
        <Icon name="arrows-rotate" size={13} color={colors.text_color_secondary} />
        <Text size={compact ? 'xs' : 's'} color="secondary" style={styles.refreshText}>
          {refreshCopy}
        </Text>
      </View>


      <View style={styles.optionCard}>
        <View style={styles.optionHeader}>
          <View style={[styles.optionIcon, { backgroundColor: colors.accent_color_primary + '18' }]}>
            <Icon name="coins" size={compact ? 13 : 15} color={colors.accent_color_primary} solid />
          </View>
          <View style={styles.optionText}>
            <Text size={compact ? 's' : 'n'} weight="semibold" color="primary">
              {t('plusOutOfCredits.buyTitle')}
            </Text>
            {!compact && (
              <Text size="xs" color="secondary">
                {t('plusOutOfCredits.buySubtitle')}
              </Text>
            )}
          </View>
        </View>
        {displayPacks.length > 0 ? (
          <>
            <View style={styles.packRow}>
              {displayPacks.map((pack) => (
                <Pressable
                  key={pack.id}
                  onPress={() => handleSelectPack(pack)}
                  disabled={loadingPackId !== null}
                  accessibilityRole="button"
                  accessibilityLabel={`${pack.credits?.toLocaleString()} credits for ${getPackPrice(pack)}`}
                  style={({ hovered }) => [
                    styles.pack,
                    pack.flags?.isPopular && styles.packPopular,
                    hovered && styles.packHovered,
                  ]}
                >
                  {pack.flags?.isPopular && (
                    <View style={styles.packBadge}>
                      <Text size="xxs" weight="bold">{t('messages.creditsDepleted.popular')}</Text>
                    </View>
                  )}
                  <Text size={compact ? 's' : 'n'} weight="bold" color="primary">
                    {pack.credits?.toLocaleString()}
                  </Text>
                  <Text size="xxs" color="secondary">{t('messages.creditsDepleted.credits')}</Text>
                  <Text size="xs" weight="semibold" color="primary" style={{ marginTop: 2 }}>
                    {loadingPackId === pack.id ? '...' : getPackPrice(pack)}
                  </Text>
                </Pressable>
              ))}
            </View>
            <Pressable onPress={handleBuyCredits} style={{ alignSelf: 'center', paddingVertical: 2 }}>
              <Text size="xs" color="secondary" style={styles.linkText}>
                {t('plusOutOfCredits.seeAllPacks')}
              </Text>
            </Pressable>
          </>
        ) : (
          <Button
            variant="primary"
            text={t('plusOutOfCredits.buyCredits')}
            size="small"
            prependIcon="coins"
            onPress={handleBuyCredits}
            isFullWidth
          />
        )}
      </View>

      {!!purchaseError && (
        <View accessibilityRole="alert" accessibilityLiveRegion="polite">
          <Text size="xs" color="secondary">{purchaseError}</Text>
        </View>
      )}

      {canUpgradeToPremium && (
        <View style={[styles.optionCard, styles.optionCardPremium]}>
          <View style={styles.optionHeader}>
            <View style={[styles.optionIcon, { backgroundColor: PREMIUM_PURPLE }]}>
              <Icon name="gem" size={compact ? 13 : 15} color="#FFFFFF" solid />
            </View>
            <View style={styles.optionText}>
              <Text size={compact ? 's' : 'n'} weight="semibold" color="primary">
                {t('plusOutOfCredits.premiumTitle')}
              </Text>
              <Text size="xs" color="secondary">
                {t('plusOutOfCredits.premiumMonthlySubtitle', {
                  credits: PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY,
                  price: PRICING_PLANS.plusPremium.monthlyPrice,
                })}
              </Text>
            </View>
          </View>
          <Button
            variant="plus"
            text={t('plusOutOfCredits.upgradePremium')}
            size="small"
            prependIcon="gem"
            onPress={handleUpgradePremium}
            isFullWidth
          />
        </View>
      )}

      {showArya && !isWork && (
        <View style={[styles.optionCard, styles.optionCardHighlight]}>
          <View style={styles.optionHeader}>
            <View style={[styles.optionIcon, { backgroundColor: 'rgba(34, 197, 94, 0.15)' }]}>
              <Icon name="comment" size={compact ? 13 : 15} color="#22c55e" solid />
            </View>
            <View style={styles.optionText}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Text size={compact ? 's' : 'n'} weight="semibold" color="primary">
                  {t('plusOutOfCredits.aryaTitle')}
                </Text>
                <View style={styles.freeBadge}>
                  <Text size="xxs" weight="bold" style={{ color: '#22c55e' }}>
                    {t('plusOutOfCredits.freeBadge')}
                  </Text>
                </View>
              </View>
              {!compact && (
                <Text size="xs" color="secondary">
                  {t('plusOutOfCredits.aryaSubtitle')}
                </Text>
              )}
            </View>
          </View>
          <Button
            variant="success"
            text={aryaLabel || t('plusOutOfCredits.chatWithArya')}
            size="small"
            prependIcon="comment"
            onPress={handleChatWithArya}
            isFullWidth
            accessibilityLabel={t('plusOutOfCredits.chatWithArya')}
          />
        </View>
      )}

      {showArya && isWork && (
        <Pressable onPress={handleChatWithArya} accessibilityRole="button"
          accessibilityLabel={t('plusOutOfCredits.chatWithArya')}
          style={{ alignSelf: 'center', paddingVertical: 8, paddingHorizontal: 10 }}>
          <Text size="xs" color="secondary" style={styles.linkText}>
            {t('plusOutOfCredits.aryaWorkAlternative')}
          </Text>
        </Pressable>
      )}

      {isPlusPremium && !compact && (
        <Text size="xs" color="tertiary" style={{ textAlign: 'center', lineHeight: 16 }}>
          {t('plusOutOfCredits.premiumFootnote')}
        </Text>
      )}
    </View>
  );
}

PlusOutOfCreditsOptions.propTypes = {
  compact: PropTypes.bool,
  source: PropTypes.string,
  onBeforeAction: PropTypes.func,
  showPacks: PropTypes.bool,
  showArya: PropTypes.bool,
  aryaLabel: PropTypes.string,
  packLimit: PropTypes.number,
  dialog: PropTypes.bool,
};

export default React.memo(PlusOutOfCreditsOptions);
